import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const CandidateContentPolicyPage: React.FC = () => (
  <LegalPageLayout title="Candidate Content Protection Policy" lastUpdated="June 13, 2026">
    <h2>Overview</h2>
    <p>
      SkipScreen is committed to protecting candidate-created content and ensuring that candidates
      maintain control over how their professional information is viewed and used.
    </p>
    <p>
      This Candidate Content Protection Policy governs all candidate content uploaded, shared,
      displayed, or otherwise made available through the SkipScreen platform, including but not
      limited to video responses, resumes, portfolio content, projects, accomplishments,
      certifications, profile information, documents, images, audio recordings, and other media.
    </p>
    <p>
      All users of SkipScreen agree to comply with this Policy as a condition of accessing candidate
      content.
    </p>

    <h2>Ownership of Candidate Content</h2>
    <p>Candidates retain ownership of all content they upload to SkipScreen, including:</p>
    <ul>
      <li>Screening Prompt videos</li>
      <li>Resume files</li>
      <li>Portfolio content</li>
      <li>Accomplishments</li>
      <li>Projects</li>
      <li>Certifications</li>
      <li>Profile information</li>
      <li>Images</li>
      <li>Documents</li>
      <li>Audio recordings</li>
      <li>Uploaded media</li>
    </ul>
    <p>
      Uploading content to SkipScreen does not transfer ownership rights to SkipScreen, recruiters,
      hiring teams, visitors, or any third party. Candidates remain the sole owners of their
      content.
    </p>

    <h2>Permitted Use</h2>
    <p>Candidate content may only be accessed for legitimate professional purposes, including:</p>
    <ul>
      <li>Candidate evaluation</li>
      <li>Recruiting activities</li>
      <li>Hiring decisions</li>
      <li>Professional networking activities authorized by the candidate</li>
    </ul>
    <p>
      Access to candidate content does not grant any ownership, reproduction, distribution,
      publication, commercial use, or intellectual property rights.
    </p>

    <h2>Prohibited Activities</h2>
    <p>Without the candidate&apos;s explicit written consent, users may not:</p>
    <ul>
      <li>Download candidate videos</li>
      <li>Download protected candidate documents</li>
      <li>Copy candidate content</li>
      <li>Save candidate content outside SkipScreen</li>
      <li>Record candidate videos using screen recording software</li>
      <li>Record candidate videos using external devices</li>
      <li>Capture candidate videos through screen sharing software</li>
      <li>Take screenshots for redistribution</li>
      <li>Photograph candidate content</li>
      <li>Reproduce candidate content</li>
      <li>Republish candidate content</li>
      <li>Redistribute candidate content</li>
      <li>Share candidate content outside authorized hiring processes</li>
      <li>Upload candidate content to other websites or platforms</li>
      <li>Post candidate content on social media</li>
      <li>Create derivative works from candidate content</li>
      <li>Use candidate content for training artificial intelligence systems</li>
      <li>Use candidate content for machine learning purposes</li>
      <li>Use candidate content for data mining purposes</li>
      <li>Scrape candidate content</li>
      <li>Archive candidate content after access expires</li>
      <li>Circumvent SkipScreen security controls</li>
      <li>Attempt to access content beyond the permissions granted by the platform</li>
    </ul>
    <p>These restrictions apply to:</p>
    <ul>
      <li>Public visitors</li>
      <li>Unverified users</li>
      <li>Verified recruiters</li>
      <li>Hiring managers</li>
      <li>Direct-share recipients</li>
      <li>Agencies</li>
      <li>Contractors</li>
      <li>Third-party reviewers</li>
      <li>Any other individual granted access to candidate content</li>
    </ul>

    <h2>Video Content Protection</h2>
    <p>
      SkipScreen recognizes that Screening Prompt videos are among the most personal and valuable
      forms of candidate content. Accordingly, users are strictly prohibited from:
    </p>
    <ul>
      <li>Screen recording candidate videos</li>
      <li>Recording candidate videos using phones, cameras, webcams, or external devices</li>
      <li>Capturing candidate videos during screen sharing sessions</li>
      <li>Creating copies of candidate videos in any format</li>
      <li>Distributing candidate videos to unauthorized individuals</li>
      <li>Using candidate videos for any purpose unrelated to candidate evaluation</li>
    </ul>
    <p>
      SkipScreen may implement technical measures designed to discourage unauthorized copying,
      including:
    </p>
    <ul>
      <li>Disabled download functionality</li>
      <li>Controlled video playback</li>
      <li>Watermarking</li>
      <li>Access controls</li>
      <li>Recruiter verification systems</li>
      <li>Activity monitoring</li>
      <li>Access expiration controls</li>
    </ul>
    <p>
      However, users acknowledge that no online platform can completely prevent screen recording,
      external recording, photography, or other methods of content capture. Regardless of technical
      limitations, such activities remain strictly prohibited under this Policy.
    </p>

    <h2>Resume and Document Protection</h2>
    <p>
      Resume previews and candidate documents are provided solely for candidate evaluation purposes.
    </p>
    <p>Users may not:</p>
    <ul>
      <li>Download protected documents</li>
      <li>Print protected documents</li>
      <li>Copy document content for redistribution</li>
      <li>Store documents outside authorized recruiting processes</li>
      <li>Share candidate documents with unauthorized parties</li>
    </ul>
    <p>Candidate resumes remain the property of the candidate at all times.</p>

    <h2>Artificial Intelligence and Machine Learning Restrictions</h2>
    <p>Candidate content may not be:</p>
    <ul>
      <li>Used to train artificial intelligence systems</li>
      <li>Used to train machine learning models</li>
      <li>Used for dataset creation</li>
      <li>Used for model fine-tuning</li>
      <li>Used for synthetic content generation</li>
      <li>Used for benchmarking AI systems</li>
      <li>Used for automated analysis outside legitimate recruiting activities</li>
    </ul>
    <p>
      This restriction applies regardless of whether the AI system is internal, external,
      commercial, open-source, or proprietary.
    </p>

    <h2>Recruiter Responsibilities</h2>
    <p>
      Recruiters and hiring teams may access candidate content solely for legitimate hiring and
      evaluation purposes. Recruiters agree not to:
    </p>
    <ul>
      <li>Retain copies of candidate content outside SkipScreen</li>
      <li>Share candidate content outside authorized hiring processes</li>
      <li>Transfer profile access to unauthorized individuals</li>
      <li>Continue using candidate content after access expires</li>
      <li>Misrepresent their identity or employer</li>
      <li>Attempt to bypass candidate privacy settings</li>
    </ul>
    <p>
      Recruiters remain responsible for protecting candidate information throughout the hiring
      process.
    </p>

    <h2>Access Expiration</h2>
    <p>
      Where applicable, candidate access permissions may expire based on the duration selected by
      the candidate. Once access expires, users must cease accessing, storing, sharing,
      distributing, or otherwise using candidate content obtained through SkipScreen. Expiration of
      access does not create any continuing right to retain candidate content.
    </p>

    <h2>Monitoring and Enforcement</h2>
    <p>SkipScreen reserves the right to:</p>
    <ul>
      <li>Monitor platform activity</li>
      <li>Investigate suspected violations</li>
      <li>Suspend or terminate user accounts</li>
      <li>Revoke recruiter verification status</li>
      <li>Restrict platform access</li>
      <li>Remove content</li>
      <li>Pursue available legal remedies</li>
      <li>Cooperate with law enforcement when appropriate</li>
    </ul>
    <p>
      Violations of this Policy may result in immediate suspension or permanent removal from the
      platform.
    </p>

    <h2>Reporting Violations</h2>
    <p>
      Candidates who believe their content has been copied, recorded, distributed, shared, misused,
      or otherwise accessed in violation of this Policy should contact:{' '}
      <a href="mailto:contact@useskipscreen.com">contact@useskipscreen.com</a>
    </p>
    <p>
      SkipScreen will review reported violations and take appropriate action at its sole discretion.
    </p>

    <h2>Limitation of Technical Controls</h2>
    <p>
      While SkipScreen employs reasonable security measures to protect candidate content, candidates
      acknowledge that no online platform can guarantee complete prevention of unauthorized copying,
      screen recording, photography, or misuse. Users are contractually prohibited from engaging in
      such activities regardless of whether technical measures successfully prevent them.
    </p>

    <h2>Policy Updates</h2>
    <p>
      SkipScreen may update this Candidate Content Protection Policy from time to time. Continued
      use of the platform after updates are posted constitutes acceptance of the revised Policy.
    </p>

    <h2>Contact</h2>
    <p>
      Questions regarding this Candidate Content Protection Policy may be directed to:{' '}
      <a href="mailto:contact@useskipscreen.com">contact@useskipscreen.com</a>
    </p>
  </LegalPageLayout>
);

export default CandidateContentPolicyPage;
